import React, { useState, useEffect } from 'react';
import { getFirstVisit } from '../../services/api';
import { formatNumber } from '../../utils/formatters';
import './FirstVisit.css';

/* Иконка вместо эмодзи в заголовке (см. Navbar.jsx). */
const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11.5" cy="11.5" r="9.5" /><path d="M18.5 18.5L22 22" />
  </svg>
);

/**
 * Что в первом визите связано с тем, вернётся ли гость.
 *
 * Показываем не только проценты, но и размер выборки со значимостью:
 * при сотне гостей разница в несколько пунктов — обычный случайный разброс,
 * и без этой пометки по ней начнут принимать решения.
 */
const FirstVisit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getFirstVisit();
        if (alive) setData(res.data);
      } catch (err) {
        if (alive) {
          setError(err.response?.data?.error || 'Анализ первого визита недоступен');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="panel fv-panel">
        <h2><IconSearch /> Что в первом визите приводит к возврату</h2>
        <p className="fv-loading">Считаем… первый расчёт занимает несколько минут.</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="panel fv-panel">
        <h2><IconSearch /> Что в первом визите приводит к возврату</h2>
        <p className="fv-loading">{error || 'Нет данных'}</p>
      </div>
    );
  }

  const strong = data.categories.filter((c) => c.significant);
  const shown = showAll ? data.categories : (strong.length ? strong : data.categories.slice(0, 8));
  const maxAbsLift = Math.max(1, ...data.categories.map((c) => Math.abs(c.lift)));

  return (
    <div className="panel fv-panel animate-fade-up">
      <h2><IconSearch /> Что в первом визите приводит к возврату</h2>

      <div className="fv-baseline">
        <div>
          <span className="fv-base-num">{data.baseline}%</span>
          <span className="fv-base-cap">
            новичков возвращаются — это база, с которой сравниваем
          </span>
        </div>
        <span className="fv-base-meta">
          {formatNumber(data.newcomers)} гостей · первый визит не позже
          чем {data.maturity_days} дн. назад
        </span>
      </div>

      {/* Категории */}
      <div className="fv-list">
        {shown.map((c) => (
          <div className="fv-row" key={c.name}>
            <div className="fv-name">
              {c.name}
              {c.significant && <span className="fv-badge">значимо</span>}
            </div>
            <div className="fv-track">
              <span className="fv-zero" />
              <span
                className={`fv-bar ${c.lift >= 0 ? 'fv-pos' : 'fv-neg'}`}
                style={{
                  width: `${(Math.abs(c.lift) / maxAbsLift) * 50}%`,
                  [c.lift >= 0 ? 'left' : 'right']: '50%',
                }}
              />
            </div>
            <div className="fv-nums">
              <span className={`fv-lift ${c.lift >= 0 ? 'pos' : 'neg'}`}>
                {c.lift >= 0 ? '+' : ''}{c.lift}
              </span>
              <span className="fv-rate">{c.rate}%</span>
              <span className="fv-n">n={formatNumber(c.guests)}</span>
            </div>
          </div>
        ))}
      </div>

      {data.categories.length > shown.length && (
        <button className="fv-more" onClick={() => setShowAll(true)}>
          Показать все категории ({data.categories.length})
        </button>
      )}
      {showAll && (
        <button className="fv-more" onClick={() => setShowAll(false)}>
          Свернуть до значимых
        </button>
      )}

      {/* Сумма и день недели */}
      <div className="fv-extra">
        <div>
          <h3>Сумма первого визита</h3>
          {data.spend_buckets.map((b) => (
            <div className="fv-mini" key={b.label}>
              <span className="fv-mini-name">{b.label}</span>
              <span className="fv-mini-track">
                <span className="fv-mini-fill" style={{ width: `${b.rate}%` }} />
              </span>
              <span className="fv-mini-val">{b.rate}%</span>
              <span className="fv-n">n={formatNumber(b.guests)}</span>
            </div>
          ))}
        </div>
        <div>
          <h3>День первого визита</h3>
          {data.weekdays.map((w) => (
            <div className="fv-mini" key={w.name}>
              <span className="fv-mini-name">{w.name}</span>
              <span className="fv-mini-track">
                <span className="fv-mini-fill" style={{ width: `${w.rate}%` }} />
              </span>
              <span className="fv-mini-val">{w.rate}%</span>
              <span className="fv-n">n={formatNumber(w.guests)}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="panel-note">
        Это связь, а не причина: гость мог вернуться не из-за самого заказа,
        а потому что пришёл с таким намерением. Показаны категории,
        встретившиеся не реже чем у {data.min_sample} новичков; пометка
        «значимо» означает, что разница с остальными не объясняется
        случайностью (p&nbsp;&lt;&nbsp;0,05).
      </p>
    </div>
  );
};

export default FirstVisit;
